<template>
  <div class="page">
    <div class="topbar">
      <div class="updated">Last updated: {{ lastUpdated }}</div>
      <div class="range">
        <el-button size="mini" :type="range === '1H' ? 'primary' : 'default'" @click="range = '1H'">1H</el-button>
        <el-button size="mini" :type="range === '24H' ? 'primary' : 'default'" @click="range = '24H'">24H</el-button>
        <el-button size="mini" :type="range === '7D' ? 'primary' : 'default'" @click="range = '7D'">7D</el-button>
        <el-button size="mini" :type="range === '30D' ? 'primary' : 'default'" @click="range = '30D'">30D</el-button>
      </div>
    </div>

    <el-row :gutter="16" class="kpi">
      <el-col :xs="24" :sm="12" :lg="6" v-for="item in stats" :key="item.key">
        <el-card class="card">
          <div class="stat-head">
            <div class="stat-icon" :style="{ background: item.bg, color: item.color }">
              <i :class="item.icon" />
            </div>
            <div class="stat-label">{{ item.label }}</div>
          </div>
          <div class="stat-foot">
            <div class="stat-value">{{ item.value }}</div>
            <div v-if="item.trend" class="stat-chip" :class="item.trend > 0 ? 'up' : 'down'">
              <i :class="item.trend > 0 ? 'el-icon-top' : 'el-icon-bottom'" />
              <span>{{ Math.abs(item.trend) }}%</span>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="charts">
      <el-col :xs="24" :lg="24">
        <el-card class="card">
          <div slot="header" class="card-header">
            <div>
              <div class="h3">Traffic Overview</div>
              <div class="sub">
                <span class="big">{{ totalTrafficPretty }}</span>
                <span class="muted">Total Traffic</span>
              </div>
            </div>
            <div class="legend">
              <span class="dot primary" /> Shared
              <span class="dot teal" /> Used
            </div>
          </div>

          <div class="chart" v-loading="chartLoading">
            <div ref="chart" class="chart-inner" />
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card class="card table-card">
      <div slot="header" class="card-header">
        <div class="h3">Recent Device Activity</div>

      </div>

      <el-table :data="recent" stripe style="width: 100%">
        <el-table-column prop="deviceId" label="Device ID" min-width="140" />
        <el-table-column prop="ip" label="IP Address" min-width="140" />
        <el-table-column prop="location" label="Location" min-width="140" />
        <el-table-column prop="bandwidth" label="Bandwidth" min-width="120" />
        <el-table-column label="Status" min-width="120">
          <template slot-scope="scope">
            <el-tag size="mini"
              :type="scope.row.status === 'Active' ? 'success' : (scope.row.status === 'Idle' ? 'warning' : 'danger')">{{
                scope.row.status }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import echarts from 'echarts'
import { getAdminBandwidthChart, getAdminBandwidthSummary } from '../../api/overview'
import { getAdminNodes, getAdminNodeSummary } from '../../api/peers'
import { getAdminProxySummary } from '../../api/proxyAdmin'

export default {
  name: 'DashboardView',
  data() {
    return {
      lastUpdated: 'Just now',
      range: '7D',
      chartLoading: false,
      chart: null,
      chartRows: [],
      stats: [
        {
          key: 'devices',
          label: 'Total Active Devices',
          value: '--',
          trend: 0,
          icon: 'el-icon-monitor',
          color: '#135bec',
          bg: 'rgba(19, 91, 236, 0.12)'
        },
        {
          key: 'proxies',
          label: 'Total Shared Proxies',
          value: '--',
          trend: 0,
          icon: 'el-icon-share',
          color: '#a855f7',
          bg: 'rgba(168, 85, 247, 0.12)'
        },
        {
          key: 'used',
          label: 'Used Bandwidth',
          value: '--',
          trend: 0,
          icon: 'el-icon-download',
          color: '#f59e0b',
          bg: 'rgba(245, 158, 11, 0.12)'
        },
        {
          key: 'shared',
          label: 'Shared Bandwidth',
          value: '--',
          trend: 0,
          icon: 'el-icon-upload',
          color: '#14b8a6',
          bg: 'rgba(20, 184, 166, 0.12)'
        }
      ],
      recent: []
    }
  },
  computed: {
    totalTrafficBytes() {
      return (this.chartRows || []).reduce((sum, r) => {
        return sum + Number(r.in || 0) + Number(r.out || 0)
      }, 0)
    },
    totalTrafficPretty() {
      return this.fmtBytes(this.totalTrafficBytes)
    }
  },
  watch: {
    range() {
      this.fetchChart()
    }
  },
  mounted() {
    this.initChart()
    this.fetchChart()
    this.fetchNodeSummary()
    this.fetchProxySummary()
    this.fetchBandwidthSummary()
    this.fetchRecentPeers()
    window.addEventListener('resize', this.onResize, false)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize, false)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    async fetchRecentPeers() {
      try {
        const res = await getAdminNodes({ size: 5, order_by: 'created_at', order_dir: 'desc' })
        const items = (res && res.data && res.data.items) ? res.data.items : []

        this.recent = items.map(p => ({
          deviceId: p.guid || p.id,
          ip: p.ip || '',
          location: (p.country || '').toUpperCase(),
          bandwidth: this.fmtBytes(Number(p.in || 0) + Number(p.out || 0)),
          status: p.status === 'active' ? 'Active' : 'Offline'
        }))
      } catch (e) {
        // giữ nguyên nếu lỗi
      }
    },
    calcTrend(today, yesterday) {
      if (!yesterday || yesterday === 0) return 0
      return Math.round((today - yesterday) / yesterday * 100)
    },
    updateStat(key, patch) {
      const idx = this.stats.findIndex(s => s.key === key)
      if (idx !== -1) {
        this.$set(this.stats, idx, { ...this.stats[idx], ...patch })
      }
    },
    async fetchNodeSummary() {
      try {
        const s = await getAdminNodeSummary()
        const d = s && s.data ? s.data : s
        this.updateStat('devices', {
          value: d.total_active != null ? String(d.total_active) : '--',
          trend: this.calcTrend(d.active_today, d.active_yesterday)
        })
      } catch (e) {
        // giữ nguyên placeholder nếu lỗi
      }
    },
    async fetchProxySummary() {
      try {
        const s = await getAdminProxySummary()
        const d = s && s.data ? s.data : s
        this.updateStat('proxies', {
          value: d.total_active != null ? String(d.total_active) : '--',
          trend: this.calcTrend(d.active_today, d.active_yesterday)
        })
      } catch (e) {
        // giữ nguyên placeholder nếu lỗi
      }
    },
    async fetchBandwidthSummary() {
      try {
        const s = await getAdminBandwidthSummary()
        console.log('[bandwidth summary raw]', s)
        const d = s && s.data ? s.data : s
        console.log('[bandwidth summary data]', d)
        this.updateStat('used', {
          value: d.total_in != null ? this.fmtBytes(d.total_in) : '--',
          trend: this.calcTrend(d.today_in, d.yesterday_in)
        })
        this.updateStat('shared', {
          value: d.total_out != null ? this.fmtBytes(d.total_out) : '--',
          trend: this.calcTrend(d.today_out, d.yesterday_out)
        })
      } catch (e) {
        console.error('[bandwidth summary error]', e)
      }
    },
    onEdit(row) {
      if (this.$message) this.$message.success('Edit ' + row.deviceId + ' (demo)')
    },
    onResize() {
      if (this.chart) this.chart.resize()
    },
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.chart.setOption(this.buildChartOption([]), true)
    },
    async fetchChart() {
      this.chartLoading = true
      try {
        const interval = this.rangeToInterval(this.range)
        const res = await getAdminBandwidthChart(interval)
        const points = (res && res.data && res.data.points) ? res.data.points : []
        this.chartRows = points
        this.lastUpdated = 'Just now'

        if (this.chart) {
          this.chart.setOption(this.buildChartOption(this.chartRows), true)
        }
      } catch (e) {
        if (this.$message) this.$message.error('Không tải được chart bandwidth')
      } finally {
        this.chartLoading = false
      }
    },
    rangeToInterval(range) {
      if (range === '1H' || range === '24H') return 'hour'
      if (range === '7D') return 'day'
      if (range === '30D') return 'month'
      return 'hour'
    },
    fmtBytes(n) {
      const num = Number(n || 0)
      const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
      let v = num
      let i = 0
      while (v >= 1024 && i < units.length - 1) {
        v = v / 1024
        i++
      }
      return (i === 0 ? String(Math.round(v)) : v.toFixed(2)) + ' ' + units[i]
    },
    buildChartOption(rows) {
      const x = (rows || []).map(r => r.label)
      const bwIn = (rows || []).map(r => Number(r.in || 0))
      const bwOut = (rows || []).map(r => Number(r.out || 0))

      return {
        color: ['#135bec', '#14b8a6'],
        grid: { left: 12, right: 12, top: 20, bottom: 24, containLabel: true },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const head = params && params[0] ? params[0].axisValue : ''
            const byName = {}
            ;(params || []).forEach(p => { byName[p.seriesName] = p.data })
            return [
              '<b>' + head + '</b>',
              'Shared: ' + this.fmtBytes(byName.Shared || 0),
              'Used: ' + this.fmtBytes(byName.Used || 0)
            ].join('<br/>')
          }
        },
        legend: { data: ['Shared', 'Used'], textStyle: { color: '#92a4c9' } },
        xAxis: {
          type: 'category',
          data: x,
          axisLine: { lineStyle: { color: '#324467' } },
          axisLabel: { color: '#92a4c9' }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#324467' } },
          splitLine: { lineStyle: { color: 'rgba(50,68,103,0.35)' } },
          axisLabel: {
            color: '#92a4c9',
            formatter: (v) => this.fmtBytes(v)
          }
        },
        series: [
          {
            name: 'Shared',
            type: 'line',
            smooth: true,
            data: bwOut,
            symbol: 'none',
            lineStyle: { width: 2, color: '#135bec' },
            itemStyle: { color: '#135bec' }
          },
          {
            name: 'Used',
            type: 'line',
            smooth: true,
            data: bwIn,
            symbol: 'none',
            lineStyle: { width: 2, color: '#14b8a6' },
            itemStyle: { color: '#14b8a6' }
          }
        ]
      }
    }
  }
}
</script>

<style scoped>
.page {
  padding: 16px;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.updated {
  font-size: 12px;
  color: #92a4c9;
}

.range {
  display: flex;
  gap: 6px;
}

.kpi {
  margin-bottom: 16px;
}

.card {
  border-radius: 12px;
  margin-top: 16px;
}

.stat-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-label {
  font-size: 13px;
  color: #92a4c9;
  font-weight: 600;
}

.stat-foot {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.stat-value {
  font-size: 22px;
  font-weight: 800;
}

.stat-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 700;
}

.stat-chip.up {
  color: #0bda5e;
  background: rgba(11, 218, 94, 0.12);
}

.stat-chip.down {
  color: #fa6238;
  background: rgba(250, 98, 56, 0.12);
}

.charts {
  margin-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.h3 {
  font-size: 16px;
  font-weight: 800;
}

.sub {
  margin-top: 4px;
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.big {
  font-size: 28px;
  font-weight: 900;
}

.muted {
  color: #92a4c9;
  font-size: 12px;
}

.legend {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #92a4c9;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
}

.dot.primary {
  background: #135bec;
}

.dot.teal {
  background: #14b8a6;
}

.chart {
  height: 260px;
}

.chart-inner {
  width: 100%;
  height: 260px;
}


.table-card /deep/ .el-card__body {
  padding-top: 0;
}

.actions {
  display: flex;
  gap: 8px;
}

.icon-ghost {
  padding: 6px;
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/index.vue